import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { LegendItem, LegendItemProps } from "./useLegendItems"
import { getIconPack, getIconUrl } from "@nova/component-map-icons"
import { defaultIconColor } from "features/map/layers/icons/constants"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { getRotatedIconsHardCodedValues, LineWidth } from "@nova/constants"
import { isLegendItemInPDFSnapshot } from "./module"
import { selectColorTheme, selectLegendPosition, selectPDFLegendSize } from "plugins/PDFv2/PDFSlice"
import { useViewport } from "features/map/GlobalViewState"
import { selectVisibleIcons } from "features/entity-caching/icons/selectors"

export const useIconLegendItems = ({ offsetX, offsetY, width, height }: LegendItemProps) => {
    const icons = useAppSelector(selectVisibleIcons)
    const legendSize = useAppSelector(selectPDFLegendSize())
    const legendPosition = useAppSelector(selectLegendPosition())
    const colorTheme = useAppSelector(selectColorTheme())

    const view = useViewport()

    const iconLegendItems = useMemo(() => {
        const iconsSet = new Set<string>()
        const items: LegendItem[] = []
        icons.forEach((icon) => {
            const setKey = `${icon.name}-${icon.url}-${icon.color}`
            if (iconsSet.has(setKey)) return
            const [x, y] = view.project([icon.longitude, icon.latitude])
            const isInsideSnapshot = isLegendItemInPDFSnapshot(
                x,
                y,
                {
                    offsetX,
                    offsetY,
                    width,
                    height,
                },
                legendSize,
                legendPosition
            )
            if (isInsideSnapshot) {
                const key = icon.url
                    .replace("/images/icons/wildfire/basic", "fireai_wildfire")
                    .replace(".svg", "")
                const pack = getIconPack(key)
                const iconUrl = getIconUrl(key) || getIconUrl("fireai_" + key)
                if (!iconUrl) {
                    return
                }
                const color =
                    pack === "shape" || pack === "numbers"
                        ? icon.color
                            ? decodeNumberToColor(icon.color)
                            : defaultIconColor
                        : undefined
                const viewbox = pack === "shape" ? "0 0 24 25" : "0 0 32 32"
                const stroke =
                    pack === "shape"
                        ? "white"
                        : pack === "numbers"
                          ? decodeNumberToColor(icon.outline || 0xff)
                          : undefined

                // swap white icons to black if light mode, black icons to white if dark mode
                const correctedColor =
                    color?.toLowerCase() === "#ffffffff" && colorTheme === "light"
                        ? "#000000"
                        : color?.toLowerCase() === "#000000ff" && colorTheme === "dark"
                          ? "#FFFFFF"
                          : color

                items.push({
                    icon: (
                        <svg
                            data-src={iconUrl}
                            height={14}
                            width={14}
                            style={{
                                fill: correctedColor as string,
                                transform: `rotate(${getRotatedIconsHardCodedValues(icon.url)}deg)`,
                            }}
                            stroke={stroke}
                            viewBox={viewbox}
                            strokeWidth={
                                icon.width === LineWidth.thick
                                    ? 1
                                    : icon.width === LineWidth.normal
                                      ? 0.5
                                      : 0
                            }
                        />
                    ),
                    title: icon.name as string,
                    id: icon.id,
                })
                iconsSet.add(setKey)
            }
        })
        return items
    }, [icons, view, offsetX, offsetY, width, height, legendSize, legendPosition, colorTheme])
    return iconLegendItems
}
